import {
  Text,
  DatePickerField,
  GreyGrey80,
  Primary13Regular,
  Primary13SemiBold,
  SimpleModal
} from '@sequencehq/core-components'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { FC, useState } from 'react'
import type { QuoteExecuteScheduleAction } from 'modules/Cube/communication/external/quotes.api.v1/ports/entitySaving/useSaveQuote'

type ExecuteNewBusinessQuoteModalProps = {
  onClose: () => void
  onConfirm: (result: {
    startDate: Date | undefined
    scheduleAction: QuoteExecuteScheduleAction
  }) => void
  submitting: boolean
}

/**
 * This dialog is used for new-business quotes: confirms a start date and creates
 * a draft billing schedule from the quote. The renewal flow uses the richer
 * two-step modal instead.
 */
export const ExecuteNewBusinessQuoteModal: FC<
  ExecuteNewBusinessQuoteModalProps
> = props => {
  const cubeContext = useCubeContext()
  const quoteData = cubeContext.queries.rawData.data
  const quoteStartDate = quoteData.common.startDate

  const [startDate, setStartDate] = useState<Date | undefined>(quoteStartDate)

  // Freeze whether the picker needs showing at open time so it doesn't
  // disappear the moment the user picks a date.
  const [requiresStartDateSelection] = useState(() => !quoteStartDate)

  const description = requiresStartDateSelection
    ? 'Select a start date to create a billing schedule from this quote.'
    : "You're about to convert this quote into a draft billing schedule."

  return (
    <SimpleModal
      isOpen
      data-testid="modal.executeQuote"
      variant="dialog"
      title="Create billing schedule"
      onClose={props.onClose}
      onSubmit={() => {
        if (startDate) {
          props.onConfirm({ startDate, scheduleAction: { mode: 'CREATE_NEW' } })
        }
        // Parent controls the modal lifecycle (it closes on a successful
        // execute), so don't self-close here.
        return false
      }}
      submitButtonText="Create billing schedule"
      cancelButtonText="Cancel"
      submitDisabled={!startDate}
      submitting={props.submitting}
      contentProps={{ minHeight: 'fit-content' }}
      width="414px"
    >
      <Text
        {...Primary13Regular}
        color={GreyGrey80}
        marginBottom={requiresStartDateSelection ? '16px' : '0'}
      >
        {description}
      </Text>
      {requiresStartDateSelection && (
        <DatePickerField
          variant="new"
          label="Start date"
          placeholder="Not set"
          data-testid="modal.executeQuote.startDate"
          value={startDate}
          onChange={setStartDate}
          styles={{
            label: { ...Primary13SemiBold, color: GreyGrey80 }
          }}
        />
      )}
    </SimpleModal>
  )
}
